/**
 * Catalogue product/variant picker for market-matched versions.
 *
 * Cascading browser (manufacturer → series → product → variant) over the
 * read-only catalogue API. Selecting a variant assigns it to a luminaire via
 * `updateLampProductVariant` (handled by the caller through `onSelect`).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader2, X } from 'lucide-react';

import { httpGET } from '../../../services/http';

type Item = { id: number; name: string };
type VariantItem = Item & { lumenOutput?: number; power?: number; ldcFileUrl?: string };

type CataloguePickerSelection = {
  manufacturerId: number;
  seriesId: number;
  productId: number;
  variantId: number;
};

export type ProductVariantSelection = CataloguePickerSelection & {
  productName: string;
  variantName: string;
};

type Props = {
  /** When set, pre-selects and expands the cascade to the assigned product/variant. */
  initialSelection?: CataloguePickerSelection | null;
  onSelect: (selection: ProductVariantSelection) => void;
  onClose: () => void;
};

const toItems = (raw: unknown): Item[] =>
  (Array.isArray(raw) ? raw : []).map((entry: { id: number | string; name: string }) => ({
    id: Number(entry.id),
    name: entry.name,
  }));

const initialSelectionKey = (selection: CataloguePickerSelection): string =>
  `${selection.manufacturerId}-${selection.seriesId}-${selection.productId}-${selection.variantId}`;

function Column<T extends Item>({
  title,
  items,
  loading,
  selectedId,
  onPick,
  renderExtra,
}: {
  title: string;
  items: T[];
  loading: boolean;
  selectedId: number | null;
  onPick: (item: T) => void;
  renderExtra?: (item: T) => React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex-1 min-w-0 flex flex-col border-r border-white/10 last:border-r-0">
      <div className="text-[10px] uppercase tracking-wider text-gray-400 px-3 py-2 border-b border-white/10 shrink-0">
        {title}
      </div>
      <div className="overflow-y-auto flex-1">
        {loading ? (
          <div className="flex items-center gap-2 text-stone-400 text-xs px-3 py-3">
            <Loader2 size={14} className="animate-spin" />
            <span>{t('common.loading', 'Loading...')}</span>
          </div>
        ) : items.length === 0 ? (
          <div className="text-xs text-gray-500 px-3 py-3">{t('workspace.productPicker.empty')}</div>
        ) : (
          items.map((item) => (
            <button
              key={item.id}
              onClick={() => onPick(item)}
              className={`w-full text-left text-xs px-3 py-2 transition-colors truncate ${
                selectedId === item.id ? 'bg-[#1a1a1a] text-[#d4ff33]' : 'text-white hover:bg-white/5'
              }`}
            >
              <span className="truncate block">{item.name}</span>
              {renderExtra?.(item)}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

export function ProductVariantPicker({ initialSelection, onSelect, onClose }: Props) {
  const { t } = useTranslation();

  const [manufacturers, setManufacturers] = useState<Item[]>([]);
  const [series, setSeries] = useState<Item[]>([]);
  const [products, setProducts] = useState<Item[]>([]);
  const [variants, setVariants] = useState<VariantItem[]>([]);

  const [selectedManufacturer, setSelectedManufacturer] = useState<Item | null>(null);
  const [selectedSeries, setSelectedSeries] = useState<Item | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Item | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);

  const [loadingLevel, setLoadingLevel] = useState<'manufacturers' | 'series' | 'products' | 'variants' | null>(
    'manufacturers',
  );
  const requestGeneration = useRef(0);
  const lastRestoredSelectionKey = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void httpGET('/catalogue/manufacturers')
      .then(({ data }) => {
        if (!cancelled) setManufacturers(toItems(data));
      })
      .catch((err) => console.error('Failed to load manufacturers:', err))
      .finally(() => {
        if (!cancelled) setLoadingLevel(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!initialSelection || manufacturers.length === 0) return;

    const selectionKey = initialSelectionKey(initialSelection);
    if (lastRestoredSelectionKey.current === selectionKey) return;

    const generation = ++requestGeneration.current;
    lastRestoredSelectionKey.current = selectionKey;
    setLoadingLevel('variants');

    const restore = async () => {
      try {
        const manufacturer = manufacturers.find((m) => m.id === initialSelection.manufacturerId);
        if (!manufacturer) return;

        const { data: seriesData } = await httpGET(`/catalogue/manufacturers/${manufacturer.id}`);
        if (generation !== requestGeneration.current) return;
        const seriesItems = toItems((seriesData as { series?: unknown })?.series);
        const seriesItem = seriesItems.find((s) => s.id === initialSelection.seriesId);
        if (!seriesItem) return;

        const { data: productsData } = await httpGET(`/catalogue/series/${seriesItem.id}`);
        if (generation !== requestGeneration.current) return;
        const productItems = toItems((productsData as { products?: unknown })?.products);
        const productItem = productItems.find((p) => p.id === initialSelection.productId);
        if (!productItem) return;

        const { data: productDetail } = await httpGET(`/catalogue/products/${productItem.id}`);
        if (generation !== requestGeneration.current) return;
        const rawVariants = (productDetail as { variants?: unknown })?.variants;
        const variantItems = (Array.isArray(rawVariants) ? rawVariants : []).map(
          (v: { id: number | string; name: string; lumenOutput?: number; power?: number; ldcFileUrl?: string }) => ({
            id: Number(v.id),
            name: v.name,
            lumenOutput: v.lumenOutput,
            power: v.power,
            ldcFileUrl: v.ldcFileUrl,
          }),
        );

        setSelectedManufacturer(manufacturer);
        setSeries(seriesItems);
        setSelectedSeries(seriesItem);
        setProducts(productItems);
        setSelectedProduct(productItem);
        setVariants(variantItems);
        setSelectedVariantId(initialSelection.variantId);
      } catch (err) {
        console.error('Failed to restore catalogue picker selection:', err);
      } finally {
        if (generation === requestGeneration.current) {
          setLoadingLevel(null);
        }
      }
    };

    void restore();
  }, [initialSelection, manufacturers]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const pickManufacturer = (manufacturer: Item) => {
    const generation = ++requestGeneration.current;
    setSelectedManufacturer(manufacturer);
    setSelectedSeries(null);
    setSelectedProduct(null);
    setSelectedVariantId(null);
    setSeries([]);
    setProducts([]);
    setVariants([]);
    setLoadingLevel('series');
    void httpGET(`/catalogue/manufacturers/${manufacturer.id}`)
      .then(({ data }) => {
        if (generation !== requestGeneration.current) return;
        setSeries(toItems((data as { series?: unknown })?.series));
      })
      .catch((err) => {
        if (generation !== requestGeneration.current) return;
        console.error('Failed to load series:', err);
      })
      .finally(() => {
        if (generation !== requestGeneration.current) return;
        setLoadingLevel(null);
      });
  };

  const pickSeries = (s: Item) => {
    const generation = ++requestGeneration.current;
    setSelectedSeries(s);
    setSelectedProduct(null);
    setSelectedVariantId(null);
    setProducts([]);
    setVariants([]);
    setLoadingLevel('products');
    void httpGET(`/catalogue/series/${s.id}`)
      .then(({ data }) => {
        if (generation !== requestGeneration.current) return;
        setProducts(toItems((data as { products?: unknown })?.products));
      })
      .catch((err) => {
        if (generation !== requestGeneration.current) return;
        console.error('Failed to load products:', err);
      })
      .finally(() => {
        if (generation !== requestGeneration.current) return;
        setLoadingLevel(null);
      });
  };

  const pickProduct = (product: Item) => {
    const generation = ++requestGeneration.current;
    setSelectedProduct(product);
    setSelectedVariantId(null);
    setVariants([]);
    setLoadingLevel('variants');
    void httpGET(`/catalogue/products/${product.id}`)
      .then(({ data }) => {
        if (generation !== requestGeneration.current) return;
        const raw = (data as { variants?: unknown })?.variants;
        const items = (Array.isArray(raw) ? raw : []).map(
          (v: { id: number | string; name: string; lumenOutput?: number; power?: number; ldcFileUrl?: string }) => ({
            id: Number(v.id),
            name: v.name,
            lumenOutput: v.lumenOutput,
            power: v.power,
            ldcFileUrl: v.ldcFileUrl,
          }),
        );
        setVariants(items);
      })
      .catch((err) => {
        if (generation !== requestGeneration.current) return;
        console.error('Failed to load variants:', err);
      })
      .finally(() => {
        if (generation !== requestGeneration.current) return;
        setLoadingLevel(null);
      });
  };

  const pickVariant = (variant: VariantItem) => {
    if (!selectedManufacturer || !selectedSeries || !selectedProduct) return;
    onSelect({
      manufacturerId: selectedManufacturer.id,
      seriesId: selectedSeries.id,
      productId: selectedProduct.id,
      variantId: variant.id,
      productName: selectedProduct.name,
      variantName: variant.name,
    });
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-[#1E1E1E] border border-white/10 rounded-xl shadow-2xl w-[min(900px,calc(100vw-48px))] h-[min(520px,calc(100dvh-96px))] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
          <h3 className="text-white text-sm font-semibold">{t('workspace.productPicker.title')}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors p-0.5">
            <X size={16} />
          </button>
        </div>
        <div className="flex flex-1 min-h-0">
          <Column
            title={t('workspace.productPicker.manufacturers')}
            items={manufacturers}
            loading={loadingLevel === 'manufacturers'}
            selectedId={selectedManufacturer?.id ?? null}
            onPick={pickManufacturer}
          />
          <Column
            title={t('workspace.productPicker.series')}
            items={series}
            loading={loadingLevel === 'series'}
            selectedId={selectedSeries?.id ?? null}
            onPick={pickSeries}
          />
          <Column
            title={t('workspace.productPicker.products')}
            items={products}
            loading={loadingLevel === 'products'}
            selectedId={selectedProduct?.id ?? null}
            onPick={pickProduct}
          />
          <Column
            title={t('workspace.productPicker.variants')}
            items={variants}
            loading={loadingLevel === 'variants'}
            selectedId={selectedVariantId}
            onPick={pickVariant}
            renderExtra={(variant) => (
              <span className="block text-[10px] text-gray-400">
                {[
                  variant.lumenOutput != null ? `${Math.round(variant.lumenOutput).toLocaleString()} lm` : null,
                  variant.power != null ? `${variant.power} W` : null,
                  !variant.ldcFileUrl ? t('workspace.productPicker.noPhotometry') : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            )}
          />
        </div>
      </div>
    </div>
  );
}
